import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WhatsAppHeader } from "@/components/whatsapp/WhatsAppWorkspace";
import { EditorSteps } from "@/components/whatsapp/WhatsAppUi";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import { FormField } from "@/components/FormField";
import { Card, SectionHeader } from "@/components/Card";
import { AudiencePicker } from "@/components/campaigns/AudiencePicker";
import { TemplatePicker } from "@/components/templates/TemplatePicker";
import {
  TemplateVariablesPanel,
  defaultMappingFor,
  extractTemplateVariables,
} from "@/components/templates/TemplateVariablesPanel";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import {
  campaignsService,
  ManualPhone,
  CreateCampaignPayload,
  AudiencePreview,
} from "@/services/campaigns.service";
import {
  Template,
  HeaderOverride,
  TemplateLocation,
} from "@/services/templates.service";
import { MediaUpload } from "@/components/MediaUpload";
import { CampaignAiAssistModal } from "@/components/campaigns/AiAssistModal";
import {
  whatsappAccountService,
  WhatsappAccount,
} from "@/services/whatsapp-account.service";
import {
  FileText,
  Users,
  Calendar,
  Eye,
  ArrowLeft,
  ArrowRight,
  Send,
  Phone,
  Image as ImageIcon,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";

export default function CampaignBuilder() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [account, setAccount] = useState<WhatsappAccount | null>(null);
  const [templateUid, setTemplateUid] = useState("");
  const [template, setTemplate] = useState<Template | null>(null);
  const [variableOverrides, setVariableOverrides] = useState<
    Record<string, string>
  >({});
  // Where each variable's value comes from: 'manual' or a contact field key.
  const [variableMappings, setVariableMappings] = useState<
    Record<string, string>
  >({});
  const [headerOverride, setHeaderOverride] = useState<HeaderOverride>({});
  const [headerOverrideEnabled, setHeaderOverrideEnabled] = useState(false);
  const [contactUids, setContactUids] = useState<string[]>([]);
  const [groupUids, setGroupUids] = useState<string[]>([]);
  const [manualPhones, setManualPhones] = useState<ManualPhone[]>([]);
  const [scheduleMode, setScheduleMode] = useState<"send_now" | "scheduled">(
    "send_now",
  );
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [savedDraftUid, setSavedDraftUid] = useState("");
  const [audiencePreview, setAudiencePreview] =
    useState<AudiencePreview | null>(null);
  const [previewPhone, setPreviewPhone] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const scheduleValid =
    scheduleMode !== "scheduled" ||
    (!!scheduledAt && new Date(scheduledAt).getTime() > Date.now());

  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    const timer = window.setTimeout(() => {
      campaignsService
        .previewAudience({
          audienceContactUids: contactUids,
          audienceGroupUids: groupUids,
          audienceManualPhones: manualPhones,
          templateVariableOverrides: variableOverrides,
          templateVariableMappings: variableMappings,
        })
        .then((result) => {
          if (cancelled) return;
          setAudiencePreview(result);
          setPreviewPhone((current) =>
            result.samples?.some((row) => row.phone === current)
              ? current
              : (result.samples?.[0]?.phone ?? ""),
          );
        })
        .catch(() => {
          if (!cancelled) setAudiencePreview(null);
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    contactUids,
    groupUids,
    manualPhones,
    variableOverrides,
    variableMappings,
  ]);
  const previewValues =
    audiencePreview?.samples?.find((row) => row.phone === previewPhone)
      ?.variables ?? variableOverrides;
  const friendlyPreviewText = (
    text: string | undefined,
    map?: Record<string, string>,
  ) =>
    text?.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, number: string) =>
      map?.[number] ? `{{${map[number]}}}` : match,
    );

  useEffect(() => {
    whatsappAccountService.get().then(setAccount);
  }, []);

  useEffect(() => {
    if (!template) return;
    setVariableOverrides({});
    setVariableMappings(
      Object.fromEntries(
        extractTemplateVariables(template).map((name) => [
          name,
          defaultMappingFor(name),
        ]),
      ),
    );
  }, [template]);

  const submit = async (action: "save_draft" | "save_and_start") => {
    setBusy(true);
    try {
      if (!account) {
        toast.error("No WhatsApp account configured");
        return;
      }
      if (!scheduleValid) {
        toast.error("Choose a future date and time");
        return;
      }
      const trimmedOverrides = Object.fromEntries(
        Object.entries(variableOverrides).filter(
          ([, v]) => v && v.trim() !== "",
        ),
      );
      // Build the header override payload only if enabled and matches the
      // template's headerType — backend rejects mismatched media.
      const ht = template?.headerType ?? "NONE";
      const overrideToSend = (() => {
        if (!headerOverrideEnabled) return undefined;
        const o: HeaderOverride = {};
        if (ht === "IMAGE" && headerOverride.imageUrl)
          o.imageUrl = headerOverride.imageUrl;
        if (ht === "VIDEO" && headerOverride.videoUrl)
          o.videoUrl = headerOverride.videoUrl;
        if (ht === "DOCUMENT" && headerOverride.documentUrl) {
          o.documentUrl = headerOverride.documentUrl;
          if (headerOverride.documentFilename)
            o.documentFilename = headerOverride.documentFilename;
        }
        if (ht === "LOCATION" && headerOverride.location)
          o.location = headerOverride.location;
        return Object.keys(o).length ? o : undefined;
      })();

      const payload: CreateCampaignPayload = {
        name,
        description: description || undefined,
        templateUid,
        whatsappAccountUid: account.accountUid,
        audienceContactUids: contactUids.length ? contactUids : undefined,
        audienceGroupUids: groupUids.length ? groupUids : undefined,
        audienceManualPhones: manualPhones.length ? manualPhones : undefined,
        templateVariableOverrides: Object.keys(trimmedOverrides).length
          ? trimmedOverrides
          : undefined,
        templateVariableMappings: Object.keys(variableMappings).length
          ? variableMappings
          : undefined,
        headerOverride: overrideToSend,
        scheduleMode,
        scheduledAt:
          scheduleMode === "scheduled" && scheduledAt
            ? new Date(scheduledAt).toISOString()
            : undefined,
      };
      const c = savedDraftUid
        ? await campaignsService.update(savedDraftUid, payload)
        : await campaignsService.create(payload);
      setSavedDraftUid(c.campaignUid);
      if (action === "save_and_start") {
        const started = await campaignsService.start(c.campaignUid);
        toast.success(
          started.status === "SCHEDULED"
            ? "Campaign scheduled"
            : "Campaign started",
        );
      } else {
        toast.success("Saved as draft");
      }
      nav(`/campaigns/${c.campaignUid}`);
    } catch {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const applyAi = (r: {
    name: string;
    description?: string;
    variableOverrides: Record<string, string>;
  }) => {
    if (r.name) setName(r.name);
    if (r.description !== undefined) setDescription(r.description ?? "");
    if (r.variableOverrides && Object.keys(r.variableOverrides).length) {
      setVariableOverrides((prev) => ({ ...prev, ...r.variableOverrides }));
    }
    setAiOpen(false);
  };

  const ready =
    name.trim().length >= 2 &&
    !!templateUid &&
    !!account &&
    scheduleValid &&
    (contactUids.length > 0 || groupUids.length > 0 || manualPhones.length > 0);

  // Step completion checks
  const step1Done = !!name;
  const step2Done = !!templateUid;
  const step3Done =
    contactUids.length > 0 || groupUids.length > 0 || manualPhones.length > 0;

  const stepDone = [step1Done, step2Done, step3Done, scheduleValid];

  return (
    <>
      <WhatsAppHeader
        section="campaigns"
        title="Create campaign"
        description="A thoughtful message, the right audience, and the perfect time."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={!templateUid}
              title={
                templateUid
                  ? "Generate name, description, and variable values with AI"
                  : "Select a template first"
              }
              onClick={() => setAiOpen(true)}
            >
              <Sparkles size={14} /> Generate with AI
            </Button>
            <Button variant="secondary" onClick={() => nav("/campaigns")}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              disabled={busy || !ready}
              onClick={() => submit("save_draft")}
            >
              Save draft
            </Button>
          </div>
        }
      />

      <CampaignAiAssistModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        templateUid={templateUid}
        templateName={template?.name}
        onApply={applyAi}
      />

      <div className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-7 sm:py-8">
        {savedDraftUid && (
          <div
            role="status"
            className="mb-4 rounded-xl border border-border bg-primary-soft p-3 text-sm"
          >
            Your draft is saved. Correct any validation errors and retry; the
            same draft will be updated.
          </div>
        )}
        <EditorSteps
          current={step}
          onChange={setStep}
          steps={[
            {
              label: "Campaign",
              description: "Name & sending account",
              done: stepDone[0],
            },
            {
              label: "Message",
              description: "Template & personalisation",
              done: stepDone[1],
            },
            {
              label: "Audience",
              description: "Contacts & groups",
              done: stepDone[2],
            },
            { label: "Review & send", description: "Timing & final review" },
          ]}
        />

        <div className="grid grid-cols-1 items-start gap-7 xl:grid-cols-[minmax(0,1fr)_380px]">
          {/* LEFT — form steps */}
          <div className="min-w-0 space-y-5">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
                Step {step + 1} of 4
              </p>
              <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
                {
                  [
                    "Give your campaign a clear purpose",
                    "Choose what your customers see",
                    "Find your people",
                    "Make every send count",
                  ][step]
                }
              </h2>
              <p className="mt-2 text-sm text-text-muted">
                {
                  [
                    "Start with a name you and your team will recognise.",
                    "Choose an approved template and personalise it for each recipient.",
                    "Bring together contacts, groups or individual phone numbers.",
                    "Review your audience, message and timing before you launch.",
                  ][step]
                }
              </p>
            </div>
            {/* Step 1: Basics */}
            {step === 0 && (
              <Card className="!p-6 !shadow-none">
                <SectionHeader
                  title="Campaign details"
                  icon={<FileText size={16} />}
                  className="mb-4"
                />
                <FormField
                  label="Campaign name"
                  htmlFor="campaign-name"
                  required
                >
                  <Input
                    id="campaign-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Black Friday — VIP customers"
                  />
                </FormField>
                <FormField label="Description">
                  <Textarea
                    aria-label="Campaign description"
                    placeholder="Add a short note for your team (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                  />
                </FormField>
                <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-alt/50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                    <Phone size={19} />
                  </span>
                  <div>
                    <p className="text-xs text-text-muted">Sending from</p>
                    <p className="mt-1 text-sm font-semibold text-text-primary">
                      {account?.businessName || "No active account"}
                    </p>
                    <p className="text-xs text-text-muted">
                      {account?.displayPhoneNumber ||
                        "Connect a number in WhatsApp Accounts."}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {step === 1 && (
              <div className="space-y-5">
                {/* Step 2: Template + Variables */}
                <Card>
                  <SectionHeader
                    title="Template"
                    icon={<FileText size={16} />}
                    className="mb-4"
                  />
                  <FormField
                    label="Approved template"
                    required
                    hint="Search by name. Only APPROVED templates can be sent."
                  >
                    <TemplatePicker
                      value={templateUid}
                      onChange={(uid, t) => {
                        setTemplateUid(uid);
                        setTemplate(t);
                        if (!uid) setVariableOverrides({});
                      }}
                      status="APPROVED"
                      accountUid={account?.accountUid}
                      placeholder="Search templates by name…"
                    />
                  </FormField>

                  {template && (
                    <div className="mt-4 border-t border-border pt-4">
                      <TemplateVariablesPanel
                        template={template}
                        values={variableOverrides}
                        onChange={setVariableOverrides}
                        showContactHints
                        mappings={variableMappings}
                        onMappingsChange={setVariableMappings}
                      />
                    </div>
                  )}
                </Card>

                {/* Header media override — only when template has a media header */}
                {template &&
                  template.type !== "CAROUSEL" &&
                  (template.headerType === "IMAGE" ||
                    template.headerType === "VIDEO" ||
                    template.headerType === "DOCUMENT" ||
                    template.headerType === "LOCATION") && (
                    <Card>
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <SectionHeader
                          title="Header media"
                          icon={<ImageIcon size={16} />}
                          className="!mb-0"
                        />
                        <label className="flex shrink-0 cursor-pointer items-center gap-2 text-xs text-text-secondary">
                          <input
                            type="checkbox"
                            checked={headerOverrideEnabled}
                            onChange={(e) => {
                              setHeaderOverrideEnabled(e.target.checked);
                              if (!e.target.checked) setHeaderOverride({});
                            }}
                            className="h-3.5 w-3.5 rounded border-border accent-primary"
                          />
                          <span className="font-medium">
                            Use a different {template.headerType?.toLowerCase()}{" "}
                            for this campaign
                          </span>
                        </label>
                      </div>
                      {!headerOverrideEnabled ? (
                        <p className="rounded-md bg-bg-soft px-3 py-2 text-xs text-text-secondary">
                          Sending with the template's saved{" "}
                          {template.headerType?.toLowerCase()}. Tick the box
                          above to swap it for this campaign — the template
                          won't be modified.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {template.headerType === "IMAGE" && (
                            <MediaUpload
                              mode="image"
                              value={headerOverride.imageUrl ?? ""}
                              onChange={(url) =>
                                setHeaderOverride((s) => ({
                                  ...s,
                                  imageUrl: url,
                                }))
                              }
                              folder="whatsapp/campaign/images"
                            />
                          )}
                          {template.headerType === "VIDEO" && (
                            <MediaUpload
                              mode="video"
                              value={headerOverride.videoUrl ?? ""}
                              onChange={(url) =>
                                setHeaderOverride((s) => ({
                                  ...s,
                                  videoUrl: url,
                                }))
                              }
                              folder="whatsapp/campaign/videos"
                            />
                          )}
                          {template.headerType === "DOCUMENT" && (
                            <MediaUpload
                              mode="document"
                              value={headerOverride.documentUrl ?? ""}
                              onChange={(url, meta) =>
                                setHeaderOverride((s) => ({
                                  ...s,
                                  documentUrl: url,
                                  documentFilename:
                                    s.documentFilename ?? meta?.name,
                                }))
                              }
                              filename={headerOverride.documentFilename}
                              onFilenameChange={(name) =>
                                setHeaderOverride((s) => ({
                                  ...s,
                                  documentFilename: name,
                                }))
                              }
                              folder="whatsapp/campaign/documents"
                            />
                          )}
                          {template.headerType === "LOCATION" && (
                            <div className="grid grid-cols-2 gap-2">
                              <FormField label="Latitude">
                                <Input
                                  type="number"
                                  step="0.000001"
                                  value={
                                    headerOverride.location?.latitude ?? ""
                                  }
                                  onChange={(e) =>
                                    setHeaderOverride((s) => ({
                                      ...s,
                                      location: {
                                        ...(s.location ?? {}),
                                        latitude:
                                          e.target.value === ""
                                            ? undefined
                                            : Number(e.target.value),
                                      },
                                    }))
                                  }
                                />
                              </FormField>
                              <FormField label="Longitude">
                                <Input
                                  type="number"
                                  step="0.000001"
                                  value={
                                    headerOverride.location?.longitude ?? ""
                                  }
                                  onChange={(e) =>
                                    setHeaderOverride((s) => ({
                                      ...s,
                                      location: {
                                        ...(s.location ?? {}),
                                        longitude:
                                          e.target.value === ""
                                            ? undefined
                                            : Number(e.target.value),
                                      },
                                    }))
                                  }
                                />
                              </FormField>
                              <div className="col-span-2">
                                <FormField label="Name">
                                  <Input
                                    value={headerOverride.location?.name ?? ""}
                                    onChange={(e) =>
                                      setHeaderOverride((s) => ({
                                        ...s,
                                        location: {
                                          ...(s.location ?? {}),
                                          name: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </FormField>
                              </div>
                              <div className="col-span-2">
                                <FormField label="Address">
                                  <Input
                                    value={
                                      headerOverride.location?.address ?? ""
                                    }
                                    onChange={(e) =>
                                      setHeaderOverride((s) => ({
                                        ...s,
                                        location: {
                                          ...(s.location ?? {}),
                                          address: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </FormField>
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            className="flex items-center gap-1 text-[11px] text-text-secondary hover:text-text-primary"
                            onClick={() => setHeaderOverride({})}
                          >
                            <RotateCcw size={11} /> Reset to template default
                          </button>
                        </div>
                      )}
                    </Card>
                  )}
              </div>
            )}
            {/* Step 3: Audience */}
            {step === 2 && (
              <Card className="!p-6 !shadow-none">
                <SectionHeader
                  title="Audience"
                  icon={<Users size={16} />}
                  className="mb-4"
                />
                <AudiencePicker
                  selectedContactUids={contactUids}
                  selectedGroupUids={groupUids}
                  manualPhones={manualPhones}
                  onChange={(next) => {
                    setContactUids(next.contactUids);
                    setGroupUids(next.groupUids);
                    setManualPhones(next.manualPhones);
                  }}
                />
                <div className="mt-4 rounded-xl bg-primary-soft px-4 py-3 text-sm text-text-secondary">
                  {previewLoading
                    ? "Calculating your audience…"
                    : `${audiencePreview?.total ?? 0} unique recipients selected`}
                  {!!audiencePreview?.invalidCount && (
                    <span className="ml-2 text-danger">
                      {audiencePreview.invalidCount} invalid numbers
                    </span>
                  )}
                </div>
              </Card>
            )}

            {/* Step 4: Schedule */}
            {step === 3 && (
              <Card className="!p-6 !shadow-none">
                <SectionHeader
                  title="Schedule"
                  icon={<Calendar size={16} />}
                  className="mb-4"
                />
                <div className="mb-5 grid grid-cols-2 gap-3">
                  {(["send_now", "scheduled"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={scheduleMode === mode}
                      onClick={() => setScheduleMode(mode)}
                      className={`rounded-xl border p-4 text-left ${scheduleMode === mode ? "border-primary bg-primary-soft" : "border-border hover:bg-surface-alt"}`}
                    >
                      {mode === "send_now" ? (
                        <Send size={19} className="mb-3 text-primary" />
                      ) : (
                        <Calendar size={19} className="mb-3 text-primary" />
                      )}
                      <span className="block text-sm font-semibold text-text-primary">
                        {mode === "send_now"
                          ? "Send now"
                          : "Schedule for later"}
                      </span>
                      <span className="mt-1 block text-xs text-text-muted">
                        {mode === "send_now"
                          ? "Start after your final review"
                          : "Choose a date and time"}
                      </span>
                    </button>
                  ))}
                </div>
                <div>
                  {scheduleMode === "scheduled" && (
                    <FormField
                      label="Run at"
                      required
                      hint={`Your time zone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}
                    >
                      {scheduledAt && !scheduleValid && (
                        <p className="mb-1 text-xs text-danger">
                          Choose a future date and time.
                        </p>
                      )}
                      <Input
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={(e) => setScheduledAt(e.target.value)}
                      />
                    </FormField>
                  )}
                </div>
                {scheduleMode === "scheduled" && (
                  <p className="mt-2 text-xs text-text-muted">
                    Recipients and template approval are checked again at the
                    scheduled time. Keep this WhatsApp number active.
                  </p>
                )}
                <dl className="mt-5 space-y-3 border-t border-border pt-5 text-sm">
                  {[
                    ["Campaign", name || "Not named"],
                    ["Template", template?.name || "Not selected"],
                    [
                      "Audience",
                      previewLoading
                        ? "Calculating…"
                        : `${audiencePreview?.total ?? 0} unique recipients`,
                    ],
                    [
                      "Sending from",
                      account?.displayPhoneNumber || "No active account",
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-5">
                      <dt className="text-text-muted">{label}</dt>
                      <dd className="break-words text-right font-medium text-text-primary">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
                {!ready && (
                  <p className="mt-4 text-sm text-text-muted">
                    Complete the campaign name, approved template, audience and
                    schedule to continue.
                  </p>
                )}
              </Card>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
              <Button
                variant="ghost"
                iconLeft={<ArrowLeft size={16} />}
                onClick={() => (step ? setStep(step - 1) : nav("/campaigns"))}
              >
                {step ? "Back" : "Cancel"}
              </Button>
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  iconRight={<ArrowRight size={16} />}
                >
                  Continue to {["message", "audience", "review"][step]}
                </Button>
              ) : (
                <Button
                  disabled={busy || !ready || previewLoading}
                  loading={busy}
                  onClick={() => submit("save_and_start")}
                  iconLeft={<Send size={16} />}
                >
                  {scheduleMode === "send_now"
                    ? "Send campaign"
                    : "Schedule campaign"}
                </Button>
              )}
            </div>
          </div>

          {/* RIGHT — sticky live preview */}
          <aside className="min-w-0 rounded-2xl border border-border bg-surface-alt/50 p-4 sm:p-6 xl:sticky xl:top-5">
            <div>
              <div>
                <SectionHeader
                  title="Live preview"
                  icon={<Eye size={16} />}
                  className="mb-4"
                />
                {previewLoading ? (
                  <p className="mb-3 text-xs text-text-muted">
                    Updating recipient preview…
                  </p>
                ) : (
                  audiencePreview && (
                    <p className="mb-3 text-xs text-text-muted">
                      {audiencePreview.total} unique recipients · Personalised
                      per contact
                      {audiencePreview.invalidCount
                        ? ` · ${audiencePreview.invalidCount} invalid numbers`
                        : ""}
                    </p>
                  )
                )}
                {!!audiencePreview?.samples?.length && (
                  <Select
                    aria-label="Preview recipient"
                    value={previewPhone}
                    onChange={(e) => setPreviewPhone(e.target.value)}
                    className="mb-4"
                    disabled={previewLoading}
                  >
                    {audiencePreview.samples.map((row) => (
                      <option key={row.phone} value={row.phone}>
                        {row.name ? `${row.name} · ` : ""}
                        {row.phone}
                      </option>
                    ))}
                  </Select>
                )}
                {template ? (
                  <div>
                    <TemplatePreview
                      headerType={
                        template.type === "CAROUSEL"
                          ? "NONE"
                          : template.headerType
                      }
                      headerText={friendlyPreviewText(
                        template.headerText,
                        template.headerVariableMap,
                      )}
                      headerImageUrl={
                        headerOverrideEnabled && headerOverride.imageUrl
                          ? headerOverride.imageUrl
                          : template.headerImageUrl
                      }
                      headerVideoUrl={
                        headerOverrideEnabled && headerOverride.videoUrl
                          ? headerOverride.videoUrl
                          : template.headerVideoUrl
                      }
                      headerDocumentUrl={
                        headerOverrideEnabled && headerOverride.documentUrl
                          ? headerOverride.documentUrl
                          : template.headerDocumentUrl
                      }
                      headerDocumentFilename={
                        headerOverrideEnabled && headerOverride.documentFilename
                          ? headerOverride.documentFilename
                          : template.headerDocumentFilename
                      }
                      headerLocation={
                        (headerOverrideEnabled && headerOverride.location
                          ? headerOverride.location
                          : template.headerLocation) as
                          | TemplateLocation
                          | undefined
                      }
                      body={
                        friendlyPreviewText(
                          template.body,
                          template.variableMap,
                        ) ?? ""
                      }
                      footer={
                        template.type === "CAROUSEL"
                          ? undefined
                          : template.footer
                      }
                      buttons={
                        template.type === "CAROUSEL" ? [] : template.buttons
                      }
                      cards={
                        template.type === "CAROUSEL"
                          ? template.cards
                          : undefined
                      }
                      samples={previewLoading ? {} : previewValues}
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-4 py-10 text-center text-xs text-text-muted">
                    Pick a template above to preview how the message will look
                    on WhatsApp.
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
